import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, FlaskConical, ShoppingBag, Trash2, Package as PackageIcon 
} from 'lucide-react';
import { getPackageImageUrl } from '../../services/HomePage/AllPackagesAPI';

export default function PackageCard({ pkg, inCart, onCartToggle, className = '' }) {
  const navigate = useNavigate();

  const pkgId = pkg.packageId || pkg.id;
  const testCount = Array.isArray(pkg.details) 
    ? pkg.details.length 
    : (pkg.testPackageDetailsJson ? (JSON.parse(pkg.testPackageDetailsJson || '[]')?.length || 12) : 12);
  
  const sellingPrice = pkg.packagePrice || pkg.price || 799;
  const mrp = pkg.totalPrice || (sellingPrice + (pkg.discountPrice || 800));
  const discount = mrp > sellingPrice 
    ? Math.round(((mrp - sellingPrice) / mrp) * 100) 
    : (pkg.discountPrice ? Math.round((pkg.discountPrice / mrp) * 100) : 35);
  
  const pkgImg = getPackageImageUrl(pkg);

  return (
    <div
      onClick={() => navigate('/package-details/' + pkgId)}
      className={'bg-white rounded-2xl border border-slate-200/90 hover:border-red-400 p-3 shadow-2xs hover:shadow-lg transition-all flex flex-col justify-between cursor-pointer group relative overflow-hidden ' + className}
    >
      <div>
        {/* Package Image Box with Product Name Fallback */}
        <div className='h-40 sm:h-44 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-2.5 relative overflow-hidden'>
          {pkgImg ? (
            <img 
              src={pkgImg} 
              alt={pkg.packageName || pkg.name || 'Package'} 
              className='max-h-full max-w-full object-contain'
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.classList.remove('hidden');
              }}
            />
          ) : null}

          {/* Styled Product Name Fallback */}
          <div 
            className={'name-fallback w-full h-full flex flex-col items-center justify-center text-center p-2.5 bg-gradient-to-br from-red-50 to-rose-100/60 rounded-lg ' + (pkgImg ? 'hidden' : 'flex')}
          >
            <div className='w-10 h-10 rounded-full bg-[#E53935] text-white flex items-center justify-center mb-1.5 shadow-md'>
              <PackageIcon size={20} />
            </div>
            <span className='text-[11px] font-black text-slate-800 line-clamp-2 leading-tight text-center px-1'>
              {pkg.packageName || pkg.name}
            </span>
          </div>

          {/* Top Discount Badge */}
          <div className='absolute top-2 left-2 flex items-center gap-1 bg-red-600 text-white text-[9px] font-extrabold px-2 py-0.5 rounded shadow-xs z-10'>
            <span>{discount}% OFF</span>
          </div>

          {/* Bottom Test Count Badge */}
          <div className='absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-xs text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs border border-blue-100 z-10'>
            <FlaskConical size={11} className='text-blue-600' />
            <span>{testCount > 0 ? (testCount + ' Tests') : 'Full Profile'}</span>
          </div>
        </div>

        {/* Package Name & Lab Tag */}
        <div className='mt-3'>
          {/* Dynamic Lab Attribution Tag */}
          <div className='inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.2 rounded mb-1 truncate max-w-full'>
            <Building2 size={10} className='shrink-0' />
            <span className='truncate'>By {pkg.assignedLabName || pkg.labName || 'Certified Partner Lab'}</span>
          </div>

          <h4 className='text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-red-600 transition-colors line-clamp-2 leading-snug'>
            {pkg.packageName || pkg.name || 'Comprehensive Full Body Checkup'}
          </h4>

          <div className='mt-2 flex items-baseline gap-1.5'>
            <span className='text-xs sm:text-sm font-extrabold text-slate-900'>₹{sellingPrice}</span>
            <span className='text-[10px] text-slate-400 line-through'>₹{mrp}</span>
          </div>
        </div>
      </div>

      {/* Bottom Cart Action Button */}
      <div className='mt-3 pt-2.5 border-t border-slate-100'>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onCartToggle) onCartToggle(pkg);
          }}
          className={'w-full py-2 px-3 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 shadow-2xs ' + (inCart ? 'bg-red-600 hover:bg-red-700 text-white border-red-600 shadow-sm' : 'bg-white text-red-600 border-red-600 hover:bg-red-50')}
        >
          {inCart ? (
            <>
              <Trash2 size={13} />
              <span>REMOVE</span>
            </>
          ) : (
            <>
              <ShoppingBag size={13} />
              <span>ADD TO CART</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
